import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import { collection, query, where, orderBy, limit, getDocs, getCountFromServer } from "firebase/firestore";
import OperationalFlow from "../OperationalFlow";
import SiteTrafficWidget from "./SiteTrafficWidget";
import api from "../../utils/api";
import RelationMapButton from "./RelationMapButton";
import {
    Users,
    Layers,
    Ship,
    ClipboardList,
    Activity,
    TrendingUp,
    DollarSign,
    AlertCircle,
    Globe,
    Zap,
    ShieldAlert,
    MessageSquare,
    Info,
    Calendar,
    CreditCard,
    RefreshCw,
} from "lucide-react";

const PlatformMetric = ({ title, value, sub, icon: Icon, color }) => (
    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex justify-between items-start mb-4">
            <div className={`p-3 rounded-xl ${color}`}>
                <Icon size={24} />
            </div>
            <Zap size={16} className="text-slate-300" />
        </div>
        <h3 className="text-slate-400 text-xs font-bold uppercase tracking-widest">{title}</h3>
        <div className="mt-1 flex items-baseline gap-2">
            <span className="text-3xl font-bold text-navy-900 font-serif">{value}</span>
            <span className="text-emerald-500 text-xs font-bold">{sub}</span>
        </div>
    </div>
);

const UserActivityRow = ({ user }) => {
    const isOnline = user.status === 'online';

    return (
        <tr className="hover:bg-slate-50 transition-colors">
            <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-navy-900 text-white flex items-center justify-center font-bold text-xs shadow-lg shadow-navy-900/20 uppercase">
                        {user.name ? user.name[0] : (user.email ? user.email[0] : '?')}
                    </div>
                    <div>
                        <div className="font-bold text-navy-900 transition-colors">{user.name || "Unknown User"}</div>
                        <div className="text-[10px] text-slate-400 truncate max-w-[150px]">{user.email || "No email"}</div>
                    </div>
                </div>
            </td>
            <td className="px-6 py-4 text-sm text-slate-600 font-medium">
                {user.tenantId || user.companyId || "N/A"}
            </td>
            <td className="px-6 py-4 text-xs font-bold text-navy-700 uppercase tracking-widest">
                {user.role || "Client"}
            </td>
            <td className="px-6 py-4 text-sm text-slate-500 font-medium">
                {formatTimeAgo(user.lastLoginAt || user.createdAt)}
            </td>
            <td className="px-6 py-4 text-right flex items-center justify-end gap-2">
                <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-500' : 'bg-slate-300'}`}></div>
                <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${isOnline ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {isOnline ? 'Online' : 'Offline'}
                </span>
            </td>
        </tr>
    );
};

const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const seconds = Math.floor((new Date() - date) / 1000);

    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + " years ago";
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + " months ago";
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + " days ago";
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + " hours ago";
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + " mins ago";
    return "Just now";
};

export default function SuperAdminDashboard() {
    const navigate = useNavigate();
    const [metricsData, setMetricsData] = useState({
        activeTenants: 0,
        totalUsers: 0,
        totalShipments: 0,
        totalEntries: 0,
        openTickets: 0
    });
    const [recentUsers, setRecentUsers] = useState([]);
    const [revenueData, setRevenueData] = useState(null);
    const [revenueLoading, setRevenueLoading] = useState(true);

    const fetchRevenue = async () => {
        setRevenueLoading(true);
        try {
            const res = await api.get("/api/payments/revenue-summary");
            setRevenueData(res.data);
        } catch (err) {
            console.error("Revenue fetch failed:", err);
        } finally {
            setRevenueLoading(false);
        }
    };

    useEffect(() => { fetchRevenue(); }, []);

    useEffect(() => {
        // Counts are aggregation queries (one read per 1,000 documents) loaded once — live
        // listeners on whole collections re-read every document and used up the daily quota
        let cancelled = false;
        const count = async q => (await getCountFromServer(q)).data().count;
        (async () => {
            try {
                const tickets = collection(db, "tickets");
                const [activeTenants, totalUsers, totalShipments, allTickets, resolved, closed, recent] = await Promise.all([
                    count(query(collection(db, "companies"), where("status", "==", "active"))),
                    count(collection(db, "users")),
                    count(collection(db, "shipments")),
                    count(tickets),
                    count(query(tickets, where("status", "==", "resolved"))),
                    count(query(tickets, where("status", "==", "closed"))),
                    getDocs(query(collection(db, "users"), orderBy("createdAt", "desc"), limit(10))),
                ]);
                if (cancelled) return;
                setMetricsData(prev => ({ ...prev, activeTenants, totalUsers, totalShipments, openTickets: allTickets - resolved - closed }));
                setRecentUsers(recent.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (err) {
                console.error("Platform metrics failed:", err);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const metrics = [
        { title: "Active Tenants", value: metricsData.activeTenants.toString(), sub: "Live", icon: Globe, color: "bg-blue-50 text-blue-600" },
        { title: "Total Users", value: metricsData.totalUsers.toString(), sub: "Across all tenants", icon: Users, color: "bg-emerald-50 text-emerald-600" },
        { title: "Open Support Tickets", value: metricsData.openTickets.toString(), sub: "Needs Attention", icon: AlertCircle, color: "bg-red-50 text-red-600" },
        { title: "Global Volume", value: metricsData.totalShipments.toString(), sub: "Total Shipments", icon: Ship, color: "bg-amber-50 text-amber-600" },
    ];

    return (
        <div className="max-w-7xl mx-auto py-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="text-3xl font-bold text-navy-900 font-serif">Platform Control Center</h1>
                        <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border border-red-200">God Mode</span>
                    </div>
                    <p className="text-slate-500 mt-1">Global oversight across all tenants, revenue, and system integrity.</p>
                </div>
                <div className="flex gap-3">
                    <button className="px-4 py-2 bg-white border border-slate-300 text-navy-900 rounded-lg hover:bg-slate-50 font-bold flex items-center gap-2 transition-all shadow-sm">
                        <ShieldAlert size={18} /> Emergency Pause
                    </button>
                    <RelationMapButton size="default" label="Platform Map" />
                </div>
            </div>

            {/* Primary Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10 text-Institutional">
                {metrics.map((m, idx) => <PlatformMetric key={idx} {...m} />)}
            </div>

            {/* Main Insights Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                {/* Tenant Performance */}
                <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                    <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                        <h3 className="font-bold text-navy-900 flex items-center gap-2">
                            <Users size={20} className="text-navy-900" /> Recent User Activity
                        </h3>
                    </div>
                    <div className="overflow-y-auto h-[400px] custom-scrollbar bg-slate-50/20">
                        {/* Desktop Table */}
                        <table className="w-full text-left hidden md:table">
                            <thead className="text-[10px] font-bold text-slate-400 uppercase tracking-wider sticky top-0 bg-white z-10 border-b border-slate-100">
                                <tr>
                                    <th className="px-6 py-4">User</th>
                                    <th className="px-6 py-4">Tenant</th>
                                    <th className="px-6 py-4">Role</th>
                                    <th className="px-6 py-4">Last Login</th>
                                    <th className="px-6 py-4 text-right">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {recentUsers.length > 0 ? recentUsers.map(user => (
                                    <UserActivityRow key={user.id} user={user} />
                                )) : (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-8 text-center text-slate-500 font-bold text-sm">
                                            No user activity found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {/* Mobile Cards */}
                        <div className="block md:hidden p-4 space-y-4">
                            {recentUsers.length > 0 ? recentUsers.map(user => {
                                const isOnline = user.status === 'online';
                                return (
                                    <div key={user.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col gap-3">
                                        <div className="flex justify-between items-start">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg bg-navy-900 text-white flex items-center justify-center font-bold text-sm shadow-md uppercase shrink-0">
                                                    {user.name ? user.name[0] : (user.email ? user.email[0] : '?')}
                                                </div>
                                                <div>
                                                    <div className="font-bold text-navy-900 text-sm truncate max-w-[150px]">{user.name || "Unknown User"}</div>
                                                    <div className="text-xs text-slate-400 truncate max-w-[150px]">{user.email || "No email"}</div>
                                                </div>
                                            </div>
                                            <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shrink-0 ${isOnline ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                                                {isOnline ? 'Online' : 'Offline'}
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div>
                                                <span className="text-slate-400 font-medium block">Tenant</span>
                                                <span className="font-bold text-navy-900 truncate block">{user.tenantId || user.companyId || "N/A"}</span>
                                            </div>
                                            <div>
                                                <span className="text-slate-400 font-medium block">Role</span>
                                                <span className="font-bold text-navy-700 uppercase">{user.role || "Client"}</span>
                                            </div>
                                            <div className="col-span-2 pt-2 border-t border-slate-50 mt-1">
                                                <span className="text-slate-400 font-medium block">Last Login</span>
                                                <span className="font-medium text-slate-600">{formatTimeAgo(user.lastLoginAt || user.createdAt)}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }) : (
                                <div className="py-8 text-center text-slate-500 font-bold text-sm">
                                    No user activity found.
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="p-4 border-t border-slate-100 bg-white text-center">
                        <button
                            onClick={() => navigate('/super-admin/users')}
                            className="text-sm font-bold text-navy-700 hover:text-blue-600 transition-colors"
                        >
                            View All Users
                        </button>
                    </div>
                </div>

                {/* Revenue Panel — Square connected */}
                <div className="space-y-6">
                    <div className="bg-navy-900 rounded-2xl p-6 text-white shadow-xl shadow-navy-900/30">
                        <div className="flex items-center justify-between mb-5">
                            <h4 className="font-bold flex items-center gap-2">
                                <TrendingUp size={20} className="text-emerald-400" /> Platform Revenue
                            </h4>
                            <div className="flex items-center gap-2">
                                {revenueData?.square?.status === "connected" && (
                                    <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                                        <CreditCard size={10} /> Square Live
                                    </span>
                                )}
                                <button
                                    onClick={fetchRevenue}
                                    disabled={revenueLoading}
                                    className="p-1 rounded-lg text-navy-400 hover:text-white transition-colors disabled:opacity-40"
                                    title="Refresh"
                                >
                                    <RefreshCw size={14} className={revenueLoading ? "animate-spin" : ""} />
                                </button>
                            </div>
                        </div>

                        {revenueLoading && !revenueData ? (
                            <div className="h-24 flex items-center justify-center">
                                <div className="w-5 h-5 border border-navy-600 border-t-emerald-400 rounded-full animate-spin" />
                            </div>
                        ) : revenueData ? (
                            <div className="space-y-5">
                                {/* Collected this month (from Square) + Goal bar */}
                                {(() => {
                                    const currentMonth = revenueData.monthlyHistory?.[revenueData.monthlyHistory.length - 1];
                                    const collected = revenueData.square?.status === "connected"
                                        ? (currentMonth?.total || 0)
                                        : (revenueData.mrr || 0);
                                    const GOAL = 10000;
                                    const pct = Math.min((collected / GOAL) * 100, 100);
                                    const label = revenueData.square?.status === "connected" ? "Collected This Month" : "MRR Estimate";
                                    return (
                                        <div>
                                            <div className="flex justify-between items-baseline mb-2">
                                                <span className="text-navy-300 text-xs font-bold uppercase tracking-widest">{label}</span>
                                                <div className="text-right">
                                                    <span className="font-bold font-serif text-2xl tracking-wide text-white">
                                                        A${collected.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </span>
                                                    <span className="text-navy-400 text-xs ml-1">/ A${GOAL.toLocaleString()} goal</span>
                                                </div>
                                            </div>
                                            <div className="w-full bg-navy-800 h-2.5 rounded-full overflow-hidden">
                                                <div
                                                    className="bg-gradient-to-r from-emerald-500 to-emerald-400 h-full rounded-full transition-all duration-1000"
                                                    style={{ width: `${pct}%` }}
                                                />
                                            </div>
                                            <div className="flex justify-between mt-1">
                                                <p className="text-[10px] text-navy-500">{currentMonth?.label || "Current month"}</p>
                                                <p className="text-[10px] text-navy-400 font-bold">{pct.toFixed(1)}% of goal</p>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Square collected */}
                                {revenueData.square?.status === "connected" && (
                                    <div className="bg-navy-800/60 rounded-xl p-3 flex justify-between items-center">
                                        <div>
                                            <p className="text-[10px] text-navy-400 font-bold uppercase tracking-widest">Square Collected (12mo)</p>
                                            <p className="text-lg font-bold font-serif text-white mt-0.5">
                                                A${(revenueData.square.totalCollected || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-[10px] text-navy-400 font-bold uppercase tracking-widest">Payments</p>
                                            <p className="text-lg font-bold text-emerald-400 mt-0.5">{revenueData.square.count}</p>
                                        </div>
                                    </div>
                                )}

                                {/* ARR + ARPA */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-navy-800/40 rounded-xl p-3">
                                        <p className="text-[10px] text-navy-400 font-bold uppercase tracking-widest">ARR</p>
                                        <p className="text-base font-bold text-white font-serif mt-0.5">
                                            A${(revenueData.arr || 0).toLocaleString()}
                                        </p>
                                    </div>
                                    <div className="bg-navy-800/40 rounded-xl p-3">
                                        <p className="text-[10px] text-navy-400 font-bold uppercase tracking-widest">ARPA</p>
                                        <p className="text-base font-bold text-white font-serif mt-0.5">
                                            A${(revenueData.arpa || 0).toLocaleString()}<span className="text-navy-400 text-xs">/mo</span>
                                        </p>
                                    </div>
                                </div>

                                {/* Plan breakdown */}
                                {revenueData.planBreakdown?.filter(p => p.count > 0).length > 0 && (
                                    <div className="space-y-2 pt-1 border-t border-navy-800">
                                        {revenueData.planBreakdown.filter(p => p.count > 0).map(p => (
                                            <div key={p.plan} className="flex items-center justify-between text-xs">
                                                <span className="text-navy-300">{p.label}</span>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-navy-400">{p.count} tenant{p.count !== 1 ? "s" : ""}</span>
                                                    <span className="text-white font-bold font-mono">A${p.mrr.toLocaleString()}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <p className="text-[10px] text-navy-500">
                                    {revenueData.tenantCount} active tenant{revenueData.tenantCount !== 1 ? "s" : ""} · Churn {revenueData.churnRate}% · Square {revenueData.square?.env || "sandbox"}
                                </p>
                            </div>
                        ) : (
                            <p className="text-navy-400 text-sm">Unable to load revenue data.</p>
                        )}
                    </div>
                </div>

            </div>

            {/* Operational Flow */}
            <div className="mt-10 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <OperationalFlow />
            </div>

            {/* Website Traffic */}
            <div className="mt-8">
                <SiteTrafficWidget />
            </div>
        </div>
    );
}
